/**
 * @license
 * Copyright (c) %FIRST_YEAR% %CURRENT_YEAR%, Oracle and/or its affiliates.
 * The Universal Permissive License (UPL), Version 1.0
 * as shown at https://oss.oracle.com/licenses/upl/
 * @ignore
 */

import { useMemo } from 'preact/hooks';
import { DataState } from 'src/UNSAFE_Collection';
import { Item } from '../utils/UNSAFE_dataProvider';
import { useEffectEvent } from '#hooks/PRIVATE_useEffectEvent';

type Props<K, D> = {
  isDropdownSelectedOnlyView?: boolean;
  valueItems?: Item<K, D>[];
};

/**
 * Get the data to show in the dropdown for the selected-only view.
 * While the component is showing only selected values in the dropdown, that list of values should
 * not change as the user deselects items within it.  For example, if the list initially shows
 * selected items "Item A" and "Item B", and the user deselects "Item B", we do not want to remove
 * "Item B" from the list.  The reason is that the user may want to reselect it again.  So the
 * list should remain static as of the time the user toggled the selected-only view on.
 * If isDropdownSelectedOnlyView is false, then the data returned from this hook will be undefined.
 * If isDropdownSelectedOnlyView is true, then the data returned from this hook will consist of
 * all of the valueItems that were selected at the time isDropdownSelectedOnlyView was set to true.
 * @param isDropdownSelectedOnlyView Whether the component is showing only selected
 * values in the dropdown: true if so, false if not.
 * @param valueItems The keys, data, and optional metadata for the selected values.
 * @returns An object with data and onLoadRange properties that can be passed on to the
 * list in the dropdown.
 */
export function useSelectedOnlyData<K, D>({
  isDropdownSelectedOnlyView = false,
  valueItems
}: Props<K, D>) {
  const STABLE_dataStateFactory = useEffectEvent((isDropdownSelectedOnlyView: boolean) =>
    !isDropdownSelectedOnlyView || !valueItems || valueItems.length === 0
      ? undefined
      : ({
          offset: 0,
          totalSize: valueItems.length,
          sizePrecision: 'exact',
          data: valueItems.map((valueItem: Item<K, D>) => {
            return { data: valueItem.data, metadata: valueItem.metadata ?? { key: valueItem.key } };
          })
          // Need to specify type of useMemo here so that dependent code in SelectMultiple and
          // useSelectMultiple doesn't show a type error related to the sizePrecision prop on DataState:
          //   Type 'string' is not assignable to type '"exact" | "atLeast"'.
        } as DataState<K, D>)
  );

  // Note that useMemo here only depends on isDropdownSelectedOnlyView because we only want to
  // recalculate when that value changes.  While the selected only view is shown, we want the
  // data set to remain static with the set of valueItems as of the time the view was changed to
  // only show selected items so that the user can deselect and reselect items.
  // If useMemo depended on the valueItems too, then the data set would change as the user
  // deselected items, which means those items would disappear from the dropdown and the user
  // could not reselect them.
  // STABLE_dataStateFactory is guaranteed to be stable, so its reference will not change between
  // renders.
  const data = useMemo(() => {
    return STABLE_dataStateFactory(isDropdownSelectedOnlyView);
  }, [STABLE_dataStateFactory, isDropdownSelectedOnlyView]);

  return {
    data,
    onLoadRange: undefined
  };
}
